// Shared utility: resolve a track or mix to a playable musicSrc.
// Tries full playback (YouTube → SoundCloud) first, falls back to snippet.

import { getCachedYoutubeId, setCachedYoutubeId, resolveTrackLinkLazy, parseDurationSec } from './lazyResolveCache';
import { resolveMixLinkLazy } from './lazyMixResolveCache';
import { isSamplesOnly } from './webOnly';
import { rememberApplePageUrl } from './applePageUrlCache';
import { entityId } from './entityId';

// Hard cap on the lazy-resolve wait during the autoplay queue-up. The
// click-handler version uses 5s; autoplay can afford the same — it just
// delays the gap between songs by a few seconds and the user wasn't going
// to perceive instant continuity at the end of a 4-minute track anyway.
const AUTOPLAY_LAZY_RESOLVE_TIMEOUT_MS = 5000;

// Each HLS track needs a unique musicSrc so jinke's library doesn't dedup
// queue entries (it dedups by musicSrc). Encoding the m3u8 URL into a query
// param produces a stable-but-unique URL per HLS track. The audio-server
// ignores the query string and just returns silent.wav.
export function silentUrlFor(streamUrl) {
    return `http://localhost:3069/silent.wav?for=${encodeURIComponent(streamUrl)}`;
}

// The Apple sample URL for a track, across every field name the API has used
// for it. Exported because "what is this track's sample?" is asked in several
// places (native refill pool, lock-screen like resolution) and the precedence
// must not drift between them — on samples-only platforms this IS the playable
// source, so a mismatch means playing or identifying the wrong thing.
export function sampleUrlOf(track) {
    if (!track) return null;
    return track.previewUrl || track.sampleUrl || track.preview_url || track.preview || null;
}

export async function resolveTrackSrc(track) {
    const rawYt = track.youtubeId || track.youtube_id || null;
    let youtubeId = rawYt && rawYt !== '-1' ? rawYt : null;
    const soundcloudUrl = track.soundcloudUrl || track.soundcloud_url || null;
    const snippetUrl = track.previewUrl || track.sampleUrl || track.preview_url || track.preview || null;

    // Remember the Apple Music catalog deep link for this track keyed by its
    // play src, so "Listen on Apple Music" works on EVERY surface that routes a
    // track through here (feed, search, recs, set planner, liked, etc.) without
    // threading pageUrl through each addTrack call.
    const applePageUrl = track.pageUrl || track.page_url || null;
    if (applePageUrl) rememberApplePageUrl(snippetUrl || track.musicSrc, applePageUrl);

    // Samples-only platforms (iOS, Android, web, mobile web) never call
    // yt-dlp / lazy resolve. Return the Apple sample (or file path) directly
    // — every IPC method these branches reach into is Electron-desktop only.
    if (isSamplesOnly) {
        if (snippetUrl) return { musicSrc: snippetUrl, originalUrl: null };
        if (track.filePath) return { musicSrc: track.filePath, originalUrl: null };
        return null;
    }

    // A liked/library track that exists as a file on this machine plays from
    // that file (the likes API sends local_file_path, cleared when the file
    // has moved). Without this, auto-advance and the Likes refill re-downloaded
    // a track the user already has from YouTube.
    if (track.local_file_path) {
        return { musicSrc: `http://localhost:3069/audio?path=${encodeURIComponent(track.local_file_path)}`, originalUrl: null };
    }

    // Manual takedown — never attempt full playback or lazy resolve. Skip
    // straight to the sample-fallback branch below.
    if (track.playbackBlocked === true) {
        if (snippetUrl) return { musicSrc: snippetUrl, originalUrl: null };
        if (track.filePath) return { musicSrc: track.filePath, originalUrl: null };
        return null;
    }
    // Read the music-info id. `prepareForPublicApi` rewrites public_id → id on
    // responses, and ingestion boundaries normalize legacy id fields to `.id`
    // via withId. entityId() covers any object whose provenance is uncertain
    // here. Without an id, autoplay would silently fall back to sample on tracks
    // where DJPalAudioPreview clicks work — visible as: play in For You → click
    // next → sample immediately, no `[lazy] start` log in main.
    const trackId = entityId(track);

    // Autoplay path bypasses DJPalAudioPreview's click handler entirely —
    // so we plug the same lazy-resolve flow in here. Sample-only track with
    // no YT/SC link → check the cache first (cheap), then fire the resolve
    // with a bounded wait. The user clicking the same track in the feed
    // earlier may have already populated the cache; if not, we resolve now
    // and persist for next time. Time budget is short on purpose — if YT
    // search or the server is slow, autoplay falls back to the sample and
    // keeps moving.
    // Note: no sample required — a sample-less track (no Apple match, the
    // no-artwork class) is still usually resolvable via lazy YT search.
    if (!youtubeId && !soundcloudUrl && trackId) {
        const cachedYt = getCachedYoutubeId(trackId);
        if (cachedYt) {
            youtubeId = cachedYt;
        } else {
            // resolveTrackLinkLazy already reads .id, but pass the resolved id
            // explicitly so it doesn't have to guess again.
            const result = await resolveTrackLinkLazy(
                { ...track, id: trackId },
                { timeoutMs: AUTOPLAY_LAZY_RESOLVE_TIMEOUT_MS },
            );
            const newYt = result?.youtubeId || null;
            if (newYt && newYt !== '-1') youtubeId = newYt;
            else {
                // Diagnostic: lazy resolve ran but produced no YouTube id. The
                // status tells us WHY (disabled = main-process lazy config cache
                // still DISABLED; no_results/search_error = InnerTube; below_
                // threshold/server_disagrees = matcher; null = 5s timeout/bail).
                console.warn('[resolveTrackSrc] lazy resolve → no youtubeId', {
                    artist: track.artist, title: track.title,
                    status: result?.status ?? (result === null ? 'null (timeout/bail)' : 'unknown'),
                });
            }
        }
    } else if (!youtubeId && !soundcloudUrl && !trackId) {
        // Diagnostic: this is the silent-sample-fallback case we saw with
        // "Kill the Vibe — Knolldoll" after clicking next. If you see this
        // warn, the feed/queue item is missing its music-info id on every
        // id-field name we recognise.
        console.warn(
            '[resolveTrackSrc] sample-only track has no id — lazy resolve cannot fire',
            { artist: track.artist, title: track.title, keys: Object.keys(track || {}) },
        );
    }

    const fullPlayUrl = youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : soundcloudUrl;

    // Tracks play from a locally-downloaded file (served with Range support by
    // the audio server) rather than a refetched googlevideo URL — see the
    // unified download in main.js (ensureTrackAudioFile). One download serves
    // both playback and the waveform, so they can't disagree, and there's no
    // extract-then-refetch handoff to get 403'd.
    const _localAudioSrc = (filePath) => `http://localhost:3069/audio?path=${encodeURIComponent(filePath)}`;

    let extractError = null;
    if (fullPlayUrl) {
        try {
            const result = await window.electron.djpal.getTrackAudio(fullPlayUrl);
            if (result.filePath) {
                return { musicSrc: _localAudioSrc(result.filePath), originalUrl: fullPlayUrl };
            }
            extractError = result.error || 'no audio file';
            console.warn('[resolveTrackSrc] track-audio download failed', {
                artist: track.artist, title: track.title, fullPlayUrl, error: result.error,
            });
        } catch (e) {
            extractError = e?.message || String(e);
            console.warn('[resolveTrackSrc] track-audio download threw', {
                artist: track.artist, title: track.title,
                fullPlayUrl, err: extractError,
            });
        }
    }

    // Replacement path: the extract just failed. Fire the lazy-replace IPC —
    // its built-in safety net (_isDefinitivelyUnplayable whitelist + oembed
    // HEAD verifier) decides whether the dead id is actually dead before doing
    // any writeback. Rate limits / 5xx / geo blocks / network errors fall
    // through with no DB change. Mirrors DJPalAudioPreview's recovery branch
    // so the next-button autoplay path doesn't silently serve a 30s sample (or
    // skip the track entirely) for a track whose source has been taken down.
    //
    // Covers BOTH cases the manual path covers: a dead youtube_id, AND a
    // SoundCloud-only track whose SC link died (no youtube_id at all). For the
    // latter we send the sentinel `-1` so the IPC skips oembed-verify (there's
    // no real video to check) and goes straight to a YouTube search — same as
    // DJPalAudioPreview's `effectiveYoutubeId || '-1'`.
    const replaceVideoId = youtubeId || (soundcloudUrl ? '-1' : null);
    if (replaceVideoId && trackId && extractError && window.electron?.djpal?.resolveTrackLink) {
        try {
            const tokenFromStorage = (() => {
                try { return localStorage.getItem('djpal:token') || null; } catch { return null; }
            })();
            // parseDurationSec, NOT Number(): tad.duration is "4:29" text —
            // Number() NaN'd it and the replacement snapshot lost duration.
            const durSec = parseDurationSec(track.duration);
            const repl = await window.electron.djpal.resolveTrackLink({
                trackId,
                artist: track.artist || '',
                title: track.title || '',
                durationSec: durSec,
                hasAppleId: !!snippetUrl,
                token: tokenFromStorage,
                replaceVideoId,
                ytdlpError: extractError,
            });
            const newId = repl?.youtubeId || null;
            if (newId && newId !== '-1' && newId !== youtubeId) {
                setCachedYoutubeId(trackId, newId);
                const newUrl = `https://www.youtube.com/watch?v=${newId}`;
                try {
                    const result2 = await window.electron.djpal.getTrackAudio(newUrl);
                    if (result2.filePath) {
                        return { musicSrc: _localAudioSrc(result2.filePath), originalUrl: newUrl };
                    }
                    console.warn('[resolveTrackSrc] replacement download failed', {
                        artist: track.artist, title: track.title, newUrl, error: result2.error,
                    });
                } catch (e) {
                    console.warn('[resolveTrackSrc] replacement download threw', {
                        artist: track.artist, title: track.title, newUrl, err: e?.message || String(e),
                    });
                }
            }
        } catch (e) {
            console.warn('[resolveTrackSrc] replacement IPC threw', {
                artist: track.artist, title: track.title, err: e?.message || String(e),
            });
        }
    }

    if (snippetUrl) {
        console.warn('[resolveTrackSrc] falling back to snippet', {
            artist: track.artist, title: track.title,
            hadFullPlayUrl: !!fullPlayUrl, trackId: trackId || null,
        });
        return { musicSrc: snippetUrl, originalUrl: null };
    }
    if (track.filePath) return { musicSrc: track.filePath, originalUrl: null };
    return null;
}

export async function resolveMixSrc(mix) {
    if (!mix?.link) return null;
    // Manual takedown — refuse to extract a stream for this mix.
    if (mix.playbackBlocked === true) return null;

    // Each result shape needs the link it was extracted from as originalUrl so
    // the bottom player / waveform can match it. effectiveLink may differ from
    // mix.link after a successful recovery (see below).
    const _shapeMix = (result, link) => {
        if (result.isHls) {
            return {
                musicSrc: silentUrlFor(result.streamUrl),
                hlsSrc: result.streamUrl,
                isHls: true,
                originalUrl: link,
                thumbnail: result.thumbnail || null,
            };
        }
        return {
            musicSrc: result.streamUrl,
            originalUrl: link,
            thumbnail: result.thumbnail || null,
            isHls: false,
        };
    };

    let effectiveLink = mix.link;
    try {
        let result = await window.electron.djpal.extractStreamUrl(effectiveLink);

        // Recovery path: if extraction failed, ask the main process whether the
        // link is REALLY dead and (if so) find a YouTube replacement. Gated by
        // LAZY_MIX_REPLACE_MODE; bails on rate-limit signals. Mirrors the manual
        // MixPlayer flow so auto-advance doesn't silently skip a mix whose link
        // died but is recoverable. Bounded so the gap between items stays short.
        if (result.error && window.electron?.djpal?.resolveMixLink) {
            console.warn('[resolveMixSrc] extract failed, attempting lazy recovery', { title: mix.title, error: result.error });
            const recovery = await resolveMixLinkLazy(mix, {
                replaceLink: mix.link,
                extractError: result.error,
                timeoutMs: 12000,
            });
            if (recovery?.link && recovery.link !== mix.link) {
                effectiveLink = recovery.link;
                result = await window.electron.djpal.extractStreamUrl(effectiveLink);
            }
        }

        if (result.streamUrl && !result.streamUrl.includes('.mpd')) {
            return _shapeMix(result, effectiveLink);
        }
    } catch (e) {}
    return null;
}
